import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    Search,
    RefreshCw,
    Clock,
    AlertTriangle,
    Server,
    Trash2,
    Play,
    Pause,
    Edit,
    Loader2,
    Wifi,
    WifiOff,
    X,
    XCircle,
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    AlertCircle,
    Radio,
    Shield,
    ShieldCheck,
    ShieldX,
    ShieldAlert,
    ChevronDown,
    ChevronUp
} from 'lucide-react'
import { machineApi, templateApi, avatarApi, dialerApi, linksApi } from '../services/api'
import StatCards from '../components/StatCards'
import './Machines.css'

// Campaign names for stats display
const CAMPAIGN_NAMES = [
    'Medicare',
    'Final Expense',
    'Home Improvement',
    'Medicaid',
    'Real Estate',
    'Diabetes',
    'Auto',
    'MVA',
    'Med Alert',
    'ACA',
    'Solar',
    'MedBots',
    'DNC'
]

function Machines({ embedded = false }) {
    // Data states
    const [machines, setMachines] = useState([])
    const [templates, setTemplates] = useState([])
    const [avatars, setAvatars] = useState([])
    const [dialers, setDialers] = useState([])
    const [accessLinks, setAccessLinks] = useState([])
    const [servers, setServers] = useState([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)

    // Server authorization states
    const [showServersSection, setShowServersSection] = useState(true)
    const [editingServer, setEditingServer] = useState(null)
    const [serverEditName, setServerEditName] = useState('')
    const [serverEditNotes, setServerEditNotes] = useState('')

    // Toast notification
    const [toast, setToast] = useState(null)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 4000)
    }

    // Filter states
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [campaignFilter, setCampaignFilter] = useState('all')
    const [dialerFilter, setDialerFilter] = useState('all')
    const [serverFilter, setServerFilter] = useState('all')

    // Pagination
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(100)

    // Selection states
    const [selectedMachines, setSelectedMachines] = useState([])
    const [selectAll, setSelectAll] = useState(false)

    // Modal states
    const [showActivateModal, setShowActivateModal] = useState(false)
    const [selectedTemplate, setSelectedTemplate] = useState('')
    const [templateSearch, setTemplateSearch] = useState('')
    const [activating, setActivating] = useState(false)

    const [showEditModal, setShowEditModal] = useState(false)
    const [editingMachine, setEditingMachine] = useState(null)
    const [editFormData, setEditFormData] = useState({})
    const [saving, setSaving] = useState(false)

    // Auto-refresh
    const [autoRefresh, setAutoRefresh] = useState(true)

    useEffect(() => {
        loadData()
    }, [])

    useEffect(() => {
        if (!autoRefresh) return
        const interval = setInterval(() => {
            loadMachines()
        }, 10000)
        return () => clearInterval(interval)
    }, [autoRefresh])

    const loadData = async () => {
        try {
            const [machinesRes, templatesRes, avatarsRes, dialersRes, linksRes, serversRes] = await Promise.all([
                machineApi.getAll(),
                templateApi.getAll(),
                avatarApi.getAll(),
                dialerApi.getAll(),
                linksApi.getAll(),
                machineApi.getServers()
            ])

            setMachines(Array.isArray(machinesRes.data) ? machinesRes.data : [])
            setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : [])
            setAvatars(Array.isArray(avatarsRes.data) ? avatarsRes.data.filter(a => !a.archived) : [])
            setDialers(Array.isArray(dialersRes.data) ? dialersRes.data.filter(d => !d.archived) : [])
            setServers(Array.isArray(serversRes.data) ? serversRes.data : [])

            if (Array.isArray(linksRes.data) && linksRes.data.length > 0) {
                try {
                    const links = JSON.parse(linksRes.data[0].links || '[]')
                    setAccessLinks(links)
                } catch (e) {
                    setAccessLinks([])
                }
            }
        } catch (error) {
            console.error('Failed to load data:', error)
        } finally {
            setLoading(false)
        }
    }

    const loadMachines = async () => {
        try {
            const machinesRes = await machineApi.getAll()
            setMachines(Array.isArray(machinesRes.data) ? machinesRes.data : [])
        } catch (error) {
            console.error('Failed to refresh machines:', error)
        }
    }

    const refresh = async () => {
        setRefreshing(true)
        await loadData()
        setRefreshing(false)
    }

    // Get unique values for filters
    const uniqueServers = useMemo(() => {
        const serverIPs = [...new Set(machines.map(m => m.server_ip).filter(Boolean))]
        return serverIPs.sort()
    }, [machines])

    const uniqueDialers = useMemo(() => {
        const dialerNames = [...new Set(machines.map(m => m.dialer_name).filter(Boolean))]
        return dialerNames.sort()
    }, [machines])

    const uniqueCampaigns = useMemo(() => {
        const campaigns = [...new Set(machines.map(m => m.campaign_name).filter(Boolean))]
        return campaigns.sort()
    }, [machines])

    // Campaign statistics
    const campaignStats = useMemo(() => {
        const stats = {}

        CAMPAIGN_NAMES.forEach(name => {
            stats[name] = { online: 0, total: 0 }
        })

        machines.forEach(m => {
            const campaignName = m.campaign_name
            if (campaignName && stats[campaignName] !== undefined) {
                stats[campaignName].total++
                const isOnline = m.status === 'activated' && m.seconds_since_heartbeat !== null && m.seconds_since_heartbeat < 120
                if (isOnline) stats[campaignName].online++
            }
        })

        return stats
    }, [machines])

    // Total stats
    const totalStats = useMemo(() => {
        let online = 0
        let inCall = 0
        let pending = 0
        let deactivated = 0
        let awaiting = 0
        let total = machines.length

        machines.forEach(m => {
            if (m.status === 'pending') {
                pending++
            } else if (m.status === 'deactivated') {
                // Check if bot is actively polling (recent heartbeat)
                if (m.seconds_since_heartbeat !== null && m.seconds_since_heartbeat < 120) {
                    awaiting++
                } else {
                    deactivated++
                }
            } else if (m.status === 'activated') {
                if (m.seconds_since_heartbeat !== null && m.seconds_since_heartbeat < 120) {
                    if (m.monitoring_status === 'in_call') {
                        inCall++
                    } else {
                        online++
                    }
                }
            }
        })

        // activated, but no heartbeat for 2+ min
        const offline = machines.filter(m => m.status === 'activated' &&
            !(m.seconds_since_heartbeat !== null && m.seconds_since_heartbeat < 120)).length
        return { online, inCall, pending, deactivated, awaiting, offline, total }
    }, [machines])

    // Filtered and sorted machines
    const filteredMachines = useMemo(() => {
        const filtered = machines.filter(m => {
            if (statusFilter !== 'all' && m.status !== statusFilter) return false
            if (campaignFilter !== 'all' && m.campaign_name !== campaignFilter) return false
            if (dialerFilter !== 'all' && m.dialer_name !== dialerFilter) return false
            if (serverFilter !== 'all' && m.server_ip !== serverFilter) return false

            if (searchQuery) {
                const q = searchQuery.toLowerCase()
                return (
                    (m.machine_uuid || '').toLowerCase().includes(q) ||
                    (m.hostname || '').toLowerCase().includes(q) ||
                    (m.ip_address || '').toLowerCase().includes(q) ||
                    (m.server_ip || '').toLowerCase().includes(q) ||
                    (m.sip_id || '').toLowerCase().includes(q) ||
                    (m.campaign_name || '').toLowerCase().includes(q) ||
                    (m.dialer_name || '').toLowerCase().includes(q)
                )
            }
            return true
        })

        // Sort: pending first, then online (activated with recent heartbeat), then offline
        return filtered.sort((a, b) => {
            const getSortPriority = (m) => {
                if (m.status === 'pending') return 0
                const isOnline = m.status === 'activated' &&
                    m.seconds_since_heartbeat !== null &&
                    m.seconds_since_heartbeat < 120
                if (isOnline) return 1
                return 2
            }
            return getSortPriority(a) - getSortPriority(b)
        })
    }, [machines, statusFilter, campaignFilter, dialerFilter, serverFilter, searchQuery])

    // Pagination
    const totalPages = Math.ceil(filteredMachines.length / pageSize)
    const paginatedMachines = useMemo(() => {
        const start = (currentPage - 1) * pageSize
        return filteredMachines.slice(start, start + pageSize)
    }, [filteredMachines, currentPage, pageSize])

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1)
    }, [statusFilter, campaignFilter, dialerFilter, serverFilter, searchQuery, pageSize])

    const pendingCount = machines.filter(m => m.status === 'pending').length

    // Selection handlers
    const handleSelectAll = (checked) => {
        setSelectAll(checked)
        if (checked) {
            // Select all machines on current page
            setSelectedMachines(paginatedMachines.map(m => m.id))
        } else {
            setSelectedMachines([])
        }
    }

    const toggleSelect = (id) => {
        setSelectedMachines(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        )
    }

    // Check selection types for conditional actions
    const selectedDeactivated = useMemo(() => {
        return machines.filter(m => selectedMachines.includes(m.id) && m.status === 'deactivated')
    }, [selectedMachines, machines])

    const selectedPending = useMemo(() => {
        return machines.filter(m => selectedMachines.includes(m.id) && m.status === 'pending')
    }, [selectedMachines, machines])

    const selectedActivated = useMemo(() => {
        return machines.filter(m => selectedMachines.includes(m.id) && m.status === 'activated')
    }, [selectedMachines, machines])

    // Template search filter
    const filteredTemplates = useMemo(() => {
        if (!templateSearch) return templates
        const q = templateSearch.toLowerCase()
        return templates.filter(t =>
            t.name.toLowerCase().includes(q) ||
            (t.campaign_name || '').toLowerCase().includes(q)
        )
    }, [templates, templateSearch])

    const handleBulkActivate = async () => {
        if (!selectedTemplate) {
            showToast('Please select a template', 'error')
            return
        }

        if (selectedMachines.length === 0) {
            showToast('No machines selected', 'error')
            return
        }

        setActivating(true)
        try {
            // If there are deactivated machines, first reactivate them (set to pending)
            if (selectedDeactivated.length > 0) {
                const deactivatedIds = selectedDeactivated.map(m => m.id)
                await machineApi.bulkReactivate({ machine_ids: deactivatedIds })
            }

            // Now activate all selected machines with the template
            const result = await machineApi.bulkActivate({
                machine_ids: selectedMachines,
                template_id: parseInt(selectedTemplate)
            })

            showToast(`Activated ${result.data.activated} of ${result.data.total} machines`, 'success')

            setShowActivateModal(false)
            setSelectedMachines([])
            setSelectAll(false)
            setSelectedTemplate('')
            setTemplateSearch('')
            loadMachines()
        } catch (error) {
            console.error('Failed to activate:', error)
            showToast('Activation failed: ' + (error.response?.data?.error || error.message), 'error')
        } finally {
            setActivating(false)
        }
    }

    // Reactivate a single deactivated machine (sets to pending)
    const handleReactivate = async (machineId) => {
        try {
            await machineApi.reactivate(machineId)
            showToast('Machine set to pending - select a template to activate', 'success')
            loadMachines()
        } catch (error) {
            console.error('Failed to reactivate:', error)
            showToast('Reactivation failed: ' + error.message, 'error')
        }
    }

    // Bulk reactivate deactivated machines (sets to pending)
    const handleBulkReactivate = async () => {
        if (selectedDeactivated.length === 0) return

        try {
            const deactivatedIds = selectedDeactivated.map(m => m.id)
            const result = await machineApi.bulkReactivate({ machine_ids: deactivatedIds })
            showToast(`${result.data.reactivated} machines set to pending`, 'success')
            setSelectedMachines([])
            setSelectAll(false)
            loadMachines()
        } catch (error) {
            console.error('Failed to bulk reactivate:', error)
            showToast('Bulk reactivation failed: ' + error.message, 'error')
        }
    }

    // Bulk deactivate activated machines
    const handleBulkDeactivate = async () => {
        if (selectedActivated.length === 0) return
        if (!confirm(`Deactivate ${selectedActivated.length} machines? Their license credentials will be invalidated.`)) return

        try {
            let deactivatedCount = 0
            for (const machine of selectedActivated) {
                try {
                    await machineApi.deactivate(machine.id)
                    deactivatedCount++
                } catch (err) {
                    console.error(`Failed to deactivate machine ${machine.id}:`, err)
                }
            }
            showToast(`${deactivatedCount} machines deactivated`, 'success')
            setSelectedMachines([])
            setSelectAll(false)
            loadMachines()
        } catch (error) {
            console.error('Failed to bulk deactivate:', error)
            showToast('Bulk deactivation failed: ' + error.message, 'error')
        }
    }

    // Bulk delete machines
    const handleBulkDelete = async () => {
        if (selectedMachines.length === 0) return
        if (!confirm(`Delete ${selectedMachines.length} machines? This cannot be undone.`)) return

        try {
            let deletedCount = 0
            for (const machineId of selectedMachines) {
                try {
                    await machineApi.delete(machineId)
                    deletedCount++
                } catch (err) {
                    console.error(`Failed to delete machine ${machineId}:`, err)
                }
            }
            showToast(`${deletedCount} machines deleted`, 'success')
            setSelectedMachines([])
            setSelectAll(false)
            loadMachines()
        } catch (error) {
            console.error('Failed to bulk delete:', error)
            showToast('Bulk delete failed: ' + error.message, 'error')
        }
    }

    const handleDeactivate = async (machineId) => {
        if (!confirm('Deactivate this machine? Its license credentials will be invalidated.')) return

        try {
            await machineApi.deactivate(machineId)
            showToast('Machine deactivated and license invalidated', 'success')
            loadMachines()
        } catch (error) {
            console.error('Failed to deactivate:', error)
            showToast('Deactivation failed: ' + error.message, 'error')
        }
    }

    const handleDelete = async (machineId) => {
        if (!confirm('Delete this machine record? This cannot be undone.')) return

        try {
            await machineApi.delete(machineId)
            showToast('Machine deleted', 'success')
            loadMachines()
        } catch (error) {
            console.error('Failed to delete:', error)
            showToast('Delete failed: ' + error.message, 'error')
        }
    }

    // ============================================
    // SERVER AUTHORIZATION HANDLERS
    // ============================================

    const handleApproveServer = async (serverId) => {
        try {
            await machineApi.approveServer(serverId, { name: `Server ${serverId}` })
            showToast('Server approved - machines can now register', 'success')
            loadData()
        } catch (error) {
            console.error('Failed to approve server:', error)
            showToast('Failed to approve server: ' + error.message, 'error')
        }
    }

    const handleBlockServer = async (serverId) => {
        if (!confirm('Block this server? All machines from this IP will be rejected.')) return
        try {
            await machineApi.blockServer(serverId)
            showToast('Server blocked', 'success')
            loadData()
        } catch (error) {
            console.error('Failed to block server:', error)
            showToast('Failed to block server: ' + error.message, 'error')
        }
    }

    const handleDeleteServer = async (serverId) => {
        if (!confirm('Delete this server? It will need to be re-approved if machines try to register again.')) return
        try {
            await machineApi.deleteServer(serverId)
            showToast('Server deleted', 'success')
            loadData()
        } catch (error) {
            console.error('Failed to delete server:', error)
            showToast('Failed to delete server: ' + error.message, 'error')
        }
    }

    const openServerEdit = (server) => {
        setEditingServer(server)
        setServerEditName(server.name || '')
        setServerEditNotes(server.notes || '')
    }

    const handleSaveServerEdit = async () => {
        if (!editingServer) return
        try {
            await machineApi.updateServer(editingServer.id, {
                name: serverEditName,
                notes: serverEditNotes
            })
            showToast('Server updated', 'success')
            setEditingServer(null)
            loadData()
        } catch (error) {
            console.error('Failed to update server:', error)
            showToast('Failed to update server: ' + error.message, 'error')
        }
    }

    // Computed values for servers
    const pendingServers = useMemo(() => servers.filter(s => s.status === 'pending'), [servers])

    const openEditModal = (machine) => {
        setEditingMachine(machine)
        setEditFormData({
            template_id: machine.template_id ? String(machine.template_id) : '',
            password: machine.password || '',
            access_link: machine.access_link || '',
            dialer_id: machine.dialer_id ? String(machine.dialer_id) : '',
            avatar_id: machine.avatar_id ? String(machine.avatar_id) : '',
            campaign: machine.campaign || '',
            // Bot configuration
            login_method: machine.login_method || 'double-merged',
            transfer_method: machine.transfer_method || 'd1',
            xfer_disposition_selector: machine.xfer_disposition_selector || 'call-transfered',
            transcriber_type: machine.transcriber_type || 'VOSK',
            dont_wait: machine.dont_wait !== 0,
            comment_login_submit_btn: machine.comment_login_submit_btn !== 0,
            comment_2nd_hello_logic: machine.comment_2nd_hello_logic !== 0,
            cmnt_40sec_call_limit_check: machine.cmnt_40sec_call_limit_check === 1,
            dispo_in_cb_after_40sec: machine.dispo_in_cb_after_40sec === 1,
            debug_mode: machine.debug_mode === 1,
            number_of_audios: machine.number_of_audios || 1,
            _showBotConfig: false,
            _hasNoLicense: !machine.license_id
        })
        setShowEditModal(true)
    }

    // Apply template to edit form
    const applyTemplateToEditForm = (templateId) => {
        const template = templates.find(t => String(t.id) === templateId)
        if (!template) return

        setEditFormData(prev => ({
            ...prev,
            template_id: templateId,
            password: template.password === '__SAME_AS_SIP__' ? (prev.password || '') : (template.password || ''),
            access_link: template.access_link || '',
            dialer_id: template.dialer_id ? String(template.dialer_id) : '',
            avatar_id: template.avatar_id ? String(template.avatar_id) : '',
            campaign: template.campaign || '',
            login_method: template.login_method || 'double-merged',
            transfer_method: template.transfer_method || 'd1',
            xfer_disposition_selector: template.xfer_disposition_selector || 'call-transfered',
            transcriber_type: template.transcriber_type || 'VOSK',
            dont_wait: template.dont_wait !== 0 && template.dont_wait !== false,
            comment_login_submit_btn: template.comment_login_submit_btn !== 0 && template.comment_login_submit_btn !== false,
            comment_2nd_hello_logic: template.comment_2nd_hello_logic !== 0 && template.comment_2nd_hello_logic !== false,
            cmnt_40sec_call_limit_check: template.cmnt_40sec_call_limit_check === 1 || template.cmnt_40sec_call_limit_check === true,
            dispo_in_cb_after_40sec: template.dispo_in_cb_after_40sec === 1 || template.dispo_in_cb_after_40sec === true,
            debug_mode: template.debug_mode === 1 || template.debug_mode === true,
            number_of_audios: template.number_of_audios || 1
        }))
    }

    const handleSaveEdit = async () => {
        setSaving(true)
        try {
            const payload = {
                template_id: editFormData.template_id ? parseInt(editFormData.template_id) : undefined,
                password: editFormData.password,
                access_link: editFormData.access_link,
                dialer_id: editFormData.dialer_id ? parseInt(editFormData.dialer_id) : undefined,
                avatar_id: editFormData.avatar_id ? parseInt(editFormData.avatar_id) : undefined,
                campaign: editFormData.campaign,
                // Bot configuration - convert booleans to 0/1
                login_method: editFormData.login_method,
                transfer_method: editFormData.transfer_method,
                xfer_disposition_selector: editFormData.xfer_disposition_selector,
                transcriber_type: editFormData.transcriber_type,
                dont_wait: editFormData.dont_wait ? 1 : 0,
                comment_login_submit_btn: editFormData.comment_login_submit_btn ? 1 : 0,
                comment_2nd_hello_logic: editFormData.comment_2nd_hello_logic ? 1 : 0,
                cmnt_40sec_call_limit_check: editFormData.cmnt_40sec_call_limit_check ? 1 : 0,
                dispo_in_cb_after_40sec: editFormData.dispo_in_cb_after_40sec ? 1 : 0,
                debug_mode: editFormData.debug_mode ? 1 : 0,
                number_of_audios: editFormData.number_of_audios || 1
            }

            const result = await machineApi.updateLicense(editingMachine.id, payload)

            if (result.data?.sip_id) {
                showToast(`License created with SIP ${result.data.sip_id}`, 'success')
            } else {
                showToast('License updated successfully', 'success')
            }
            setShowEditModal(false)
            setEditingMachine(null)
            loadMachines()
        } catch (error) {
            console.error('Failed to save:', error)
            showToast('Update failed: ' + (error.response?.data?.error || error.message), 'error')
        } finally {
            setSaving(false)
        }
    }

    const timeAgo = (seconds) => {
        if (seconds === null || seconds === undefined) return 'Never'
        if (seconds < 60) return `${seconds}s`
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
        return `${Math.floor(seconds / 86400)}d`
    }

    const StatusBadge = ({ status, heartbeat, monitoringStatus }) => {
        const isOnline = heartbeat !== null && heartbeat !== undefined && heartbeat < 120
        const isAwaitingActivation = heartbeat !== null && heartbeat !== undefined && heartbeat < 120

        if (status === 'pending') {
            return (
                <span className="status-badge pending">
                    <Clock size={10} className="status-icon" />
                    Pending
                </span>
            )
        }
        if (status === 'activated') {
            // Check monitoring status for more detail
            if (isOnline && monitoringStatus === 'in_call') {
                return (
                    <span className="status-badge in-call">
                        <Wifi size={10} className="status-icon" />
                        In Call
                    </span>
                )
            }
            return (
                <span className={`status-badge ${isOnline ? 'online' : 'offline'}`}>
                    {isOnline ? <Wifi size={10} className="status-icon" /> : <WifiOff size={10} className="status-icon" />}
                    {isOnline ? 'Online' : 'Offline'}
                </span>
            )
        }
        if (status === 'deactivated') {
            // Show "Awaiting" if the bot is actively polling (recent heartbeat)
            if (isAwaitingActivation) {
                return (
                    <span className="status-badge awaiting">
                        <Radio size={10} className="status-icon pulse" />
                        Awaiting
                    </span>
                )
            }
            return (
                <span className="status-badge deactivated">
                    <Pause size={10} className="status-icon" />
                    Deactivated
                </span>
            )
        }
        return null
    }

    // Pagination component
    const Pagination = () => {
        const pages = []
        const maxVisiblePages = 5
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1)
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i)
        }

        return (
            <div className="pagination-container">
                <div className="pagination-info">
                    Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredMachines.length)} of {filteredMachines.length}
                </div>

                <div className="pagination-controls">
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="page-size-select"
                    >
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                    </select>

                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-btn"
                    >
                        <ChevronLeft size={16} />
                    </button>

                    <div className="pagination-pages">
                        {startPage > 1 && (
                            <>
                                <button onClick={() => setCurrentPage(1)} className="pagination-page">1</button>
                                {startPage > 2 && <span style={{ color: 'var(--text-muted)' }}>...</span>}
                            </>
                        )}
                        {pages.map(page => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`pagination-page ${page === currentPage ? 'active' : ''}`}
                            >
                                {page}
                            </button>
                        ))}
                        {endPage < totalPages && (
                            <>
                                {endPage < totalPages - 1 && <span style={{ color: 'var(--text-muted)' }}>...</span>}
                                <button onClick={() => setCurrentPage(totalPages)} className="pagination-page">{totalPages}</button>
                            </>
                        )}
                    </div>

                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="machines-page">
                <div className="loading-state" style={{ display: 'flex', justifyContent: 'center', padding: '64px' }}>
                    <div className="loading-spinner"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="machines-page">
            {/* Toast Notification */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        className={`toast ${toast.type}`}
                        initial={{ opacity: 0, y: -20, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: -20, x: '-50%' }}
                    >
                        {toast.type === 'success' ? (
                            <CheckCircle size={18} />
                        ) : (
                            <AlertCircle size={18} />
                        )}
                        {toast.message}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <div className="page-header">
                <div className="page-header-left">
                    {!embedded && <h1 className="page-title">Machines</h1>}
                    <p className="page-subtitle">
                        Old Linux bot system — manage bot machines and license assignments
                    </p>
                </div>
                <div className="page-header-actions">
                    <label className="auto-refresh-toggle">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                        />
                        Auto-refresh
                    </label>
                    <button
                        onClick={refresh}
                        className="btn btn-secondary btn-icon"
                        disabled={refreshing}
                    >
                        <RefreshCw size={18} className={refreshing ? 'spinner' : ''} />
                    </button>
                </div>
            </div>

            {/* Stat cards — click to filter by status */}
            <StatCards cards={[
                { key: 'all', label: 'All machines', value: totalStats.total, icon: Server, color: '#60a5fa',
                  onClick: () => setStatusFilter('all'), active: statusFilter === 'all' },
                { key: 'activated', label: 'Online', value: totalStats.online + totalStats.inCall, icon: Wifi, color: '#22c55e',
                  sub: `${totalStats.inCall} in call · ${totalStats.offline} activated but offline`,
                  onClick: () => setStatusFilter(f => f === 'activated' ? 'all' : 'activated'), active: statusFilter === 'activated' },
                { key: 'pending', label: 'Pending', value: totalStats.pending, icon: Clock, color: '#f59e0b',
                  sub: totalStats.awaiting ? `${totalStats.awaiting} awaiting activation` : 'waiting for approval',
                  onClick: () => setStatusFilter(f => f === 'pending' ? 'all' : 'pending'), active: statusFilter === 'pending' },
                { key: 'deactivated', label: 'Deactivated', value: totalStats.deactivated + totalStats.awaiting, icon: WifiOff, color: '#ef4444',
                  onClick: () => setStatusFilter(f => f === 'deactivated' ? 'all' : 'deactivated'), active: statusFilter === 'deactivated' }
            ]} />

            {/* Server Authorization Section */}
            {servers.length > 0 && (
                <div className="server-auth-section" style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    border: pendingServers.length > 0 ? '1px solid var(--warning)' : '1px solid var(--border-color)'
                }}>
                    <div
                        onClick={() => setShowServersSection(!showServersSection)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            marginBottom: showServersSection ? '12px' : 0
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Shield size={20} style={{ color: pendingServers.length > 0 ? 'var(--warning)' : 'var(--primary)' }} />
                            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Server Authorization</span>
                            {pendingServers.length > 0 && (
                                <span style={{
                                    background: 'var(--warning)',
                                    color: '#000',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                }}>
                                    {pendingServers.length} pending
                                </span>
                            )}
                        </div>
                        {showServersSection ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>

                    {showServersSection && (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>IP Address</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Name</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Status</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Machines</th>
                                        <th style={{ textAlign: 'left', padding: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Created</th>
                                        <th style={{ textAlign: 'right', padding: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {servers.map(server => (
                                        <tr key={server.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ padding: '10px 8px', fontFamily: 'monospace' }}>{server.ip_address}</td>
                                            <td style={{ padding: '10px 8px' }}>
                                                {editingServer?.id === server.id ? (
                                                    <input
                                                        type="text"
                                                        value={serverEditName}
                                                        onChange={e => setServerEditName(e.target.value)}
                                                        placeholder="Server name"
                                                        style={{
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid var(--border-color)',
                                                            background: 'var(--bg-primary)',
                                                            color: 'var(--text-primary)',
                                                            width: '120px'
                                                        }}
                                                    />
                                                ) : (
                                                    <span style={{ color: server.name ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                                        {server.name || 'Unnamed'}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px 8px' }}>
                                                {server.status === 'approved' && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--success)', fontSize: '0.8rem' }}>
                                                        <ShieldCheck size={14} /> Approved
                                                    </span>
                                                )}
                                                {server.status === 'pending' && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--warning)', fontSize: '0.8rem' }}>
                                                        <ShieldAlert size={14} /> Pending
                                                    </span>
                                                )}
                                                {server.status === 'blocked' && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--danger)', fontSize: '0.8rem' }}>
                                                        <ShieldX size={14} /> Blocked
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px 8px' }}>
                                                <span style={{ color: 'var(--text-secondary)' }}>
                                                    {server.machine_count || 0} ({server.active_machines || 0} active)
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 8px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                                {new Date(server.created_at).toLocaleDateString()}
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                    {editingServer?.id === server.id ? (
                                                        <>
                                                            <button
                                                                onClick={handleSaveServerEdit}
                                                                style={{
                                                                    padding: '4px 10px',
                                                                    borderRadius: '4px',
                                                                    border: 'none',
                                                                    background: 'var(--success)',
                                                                    color: '#fff',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingServer(null)}
                                                                style={{
                                                                    padding: '4px 10px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid var(--border-color)',
                                                                    background: 'transparent',
                                                                    color: 'var(--text-secondary)',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {server.status === 'pending' && (
                                                                <button
                                                                    onClick={() => handleApproveServer(server.id)}
                                                                    style={{
                                                                        padding: '4px 10px',
                                                                        borderRadius: '4px',
                                                                        border: 'none',
                                                                        background: 'var(--success)',
                                                                        color: '#fff',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.75rem',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px'
                                                                    }}
                                                                >
                                                                    <CheckCircle size={12} /> Approve
                                                                </button>
                                                            )}
                                                            {server.status !== 'blocked' && (
                                                                <button
                                                                    onClick={() => handleBlockServer(server.id)}
                                                                    style={{
                                                                        padding: '4px 10px',
                                                                        borderRadius: '4px',
                                                                        border: 'none',
                                                                        background: 'var(--danger)',
                                                                        color: '#fff',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                >
                                                                    Block
                                                                </button>
                                                            )}
                                                            {server.status === 'blocked' && (
                                                                <button
                                                                    onClick={() => handleApproveServer(server.id)}
                                                                    style={{
                                                                        padding: '4px 10px',
                                                                        borderRadius: '4px',
                                                                        border: 'none',
                                                                        background: 'var(--success)',
                                                                        color: '#fff',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                >
                                                                    Unblock
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => openServerEdit(server)}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid var(--border-color)',
                                                                    background: 'transparent',
                                                                    color: 'var(--text-secondary)',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <Edit size={12} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteServer(server.id)}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid var(--border-color)',
                                                                    background: 'transparent',
                                                                    color: 'var(--danger)',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Campaign Stats Section */}
            <div className="campaign-stats-section">
                <div className="campaign-stats-header">
                    <span className="campaign-stats-title">Campaign Stats (Online / Total)</span>
                </div>
                <div className="campaign-stats-grid">
                    {CAMPAIGN_NAMES.map(name => {
                        const stats = campaignStats[name]
                        if (stats.total === 0) return null
                        return (
                            <div
                                key={name}
                                className={`campaign-stat-chip ${campaignFilter === name ? 'active' : ''}`}
                                onClick={() => setCampaignFilter(campaignFilter === name ? 'all' : name)}
                            >
                                <span className="campaign-stat-name">{name}</span>
                                <span className="campaign-stat-counts">
                                    <span className="campaign-stat-online">{stats.online}</span>
                                    <span className="campaign-stat-divider">/</span>
                                    <span className="campaign-stat-total">{stats.total}</span>
                                </span>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Toolbar */}
            <div className="toolbar">
                <div className="toolbar-left">
                    <div className="search-wrapper">
                        <Search size={14} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="activated">Activated</option>
                        <option value="deactivated">Deactivated</option>
                    </select>

                    <select
                        value={campaignFilter}
                        onChange={(e) => setCampaignFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Campaigns</option>
                        {uniqueCampaigns.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>

                    <select
                        value={dialerFilter}
                        onChange={(e) => setDialerFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Dialers</option>
                        {uniqueDialers.map(d => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>

                    <select
                        value={serverFilter}
                        onChange={(e) => setServerFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Servers</option>
                        {uniqueServers.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div className="toolbar-right">
                    {selectedMachines.length > 0 && (
                        <>
                            {/* Bulk Delete */}
                            <button
                                onClick={handleBulkDelete}
                                className="btn btn-danger"
                                title="Delete selected machines"
                            >
                                <Trash2 size={14} />
                                Delete ({selectedMachines.length})
                            </button>

                            {/* Bulk Deactivate - only for activated machines */}
                            {selectedActivated.length > 0 && (
                                <button
                                    onClick={handleBulkDeactivate}
                                    className="btn btn-secondary"
                                    title="Deactivate selected machines"
                                >
                                    <XCircle size={14} />
                                    Deactivate ({selectedActivated.length})
                                </button>
                            )}

                            {/* If only deactivated machines selected, show option to set to pending */}
                            {selectedDeactivated.length > 0 && selectedPending.length === 0 && selectedActivated.length === 0 && (
                                <button
                                    onClick={handleBulkReactivate}
                                    className="btn btn-secondary"
                                    title="Set to pending status (will need template activation after)"
                                >
                                    <RefreshCw size={14} />
                                    Set Pending ({selectedDeactivated.length})
                                </button>
                            )}

                            {/* Activate button - for pending and deactivated */}
                            {(selectedPending.length > 0 || selectedDeactivated.length > 0) && (
                                <button
                                    onClick={() => {
                                        // Pre-select template if selected machines have one
                                        const machinesWithTemplate = selectedPending.filter(m => m.template_id)
                                        if (machinesWithTemplate.length === 1) {
                                            setSelectedTemplate(String(machinesWithTemplate[0].template_id))
                                        } else {
                                            setSelectedTemplate('')
                                        }
                                        setShowActivateModal(true)
                                    }}
                                    className="btn btn-primary"
                                >
                                    <Play size={14} />
                                    Activate ({selectedPending.length + selectedDeactivated.length})
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Pending Alert */}
            {pendingCount > 0 && (
                <div className="pending-alert">
                    <AlertTriangle size={18} className="pending-alert-icon" />
                    <div className="pending-alert-content">
                        <span className="pending-alert-title">{pendingCount} machines waiting</span>
                        <span className="pending-alert-text">Select and apply a template to activate</span>
                    </div>
                </div>
            )}

            {/* Machines Table */}
            <div className="table-container">
                <div className="table-scroll">
                    <table className="table">
                        <thead>
                            <tr>
                                <th className="checkbox-cell">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                </th>
                                <th>Hostname</th>
                                <th>Server IP</th>
                                <th>VM IP</th>
                                <th>Status</th>
                                <th>SIP ID</th>
                                <th>Campaign</th>
                                <th>Dialer</th>
                                <th>Last Seen</th>
                                <th className="actions-col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedMachines.map(machine => (
                                <motion.tr
                                    key={machine.id}
                                    className={selectedMachines.includes(machine.id) ? 'selected' : ''}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                >
                                    <td className="checkbox-cell">
                                        <input
                                            type="checkbox"
                                            checked={selectedMachines.includes(machine.id)}
                                            onChange={() => toggleSelect(machine.id)}
                                        />
                                    </td>
                                    <td>
                                        <span className="machine-hostname">{machine.hostname || 'Unknown'}</span>
                                    </td>
                                    <td>
                                        <span className="server-address" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{machine.server_ip || '-'}</span>
                                    </td>
                                    <td>
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{machine.ip_address || '-'}</span>
                                    </td>
                                    <td>
                                        <StatusBadge
                                            status={machine.status}
                                            heartbeat={machine.seconds_since_heartbeat}
                                            monitoringStatus={machine.monitoring_status}
                                        />
                                    </td>
                                    <td>
                                        {machine.status === 'pending' && machine.template_name ? (
                                            <span
                                                className="template-badge"
                                                title={`Pre-selected template: ${machine.template_name}`}
                                                style={{
                                                    background: 'rgba(59, 130, 246, 0.1)',
                                                    color: 'var(--primary)',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                {machine.template_name}
                                            </span>
                                        ) : (
                                            <span className={`sip-id ${!machine.sip_id ? 'empty' : ''}`}>
                                                {machine.sip_id || '-'}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`cell-text ${!machine.campaign_name ? 'empty' : ''}`}>
                                            {machine.campaign_name || '-'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`cell-text ${!machine.dialer_name ? 'empty' : ''}`}>
                                            {machine.dialer_name || '-'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`last-seen ${machine.seconds_since_heartbeat < 120 ? 'recent' : machine.seconds_since_heartbeat < 600 ? 'stale' : ''}`}>
                                            {timeAgo(machine.seconds_since_heartbeat)}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="actions-cell">
                                            {/* Activated machine actions */}
                                            {machine.status === 'activated' && (
                                                <>
                                                    <button
                                                        onClick={() => openEditModal(machine)}
                                                        className="action-btn edit"
                                                        title="Edit license"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeactivate(machine.id)}
                                                        className="action-btn deactivate"
                                                        title="Deactivate"
                                                    >
                                                        <Pause size={14} />
                                                    </button>
                                                </>
                                            )}
                                            {/* Pending machine actions - can edit to activate with template */}
                                            {machine.status === 'pending' && (
                                                <button
                                                    onClick={() => openEditModal(machine)}
                                                    className="action-btn edit"
                                                    title="Edit/Activate with template"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                            )}
                                            {/* Deactivated machine actions - reactivate button */}
                                            {machine.status === 'deactivated' && (
                                                <button
                                                    onClick={() => handleReactivate(machine.id)}
                                                    className="action-btn edit"
                                                    title="Set to pending for reactivation"
                                                >
                                                    <Play size={14} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDelete(machine.id)}
                                                className="action-btn delete"
                                                title="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredMachines.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <Server size={32} />
                        </div>
                        <h3 className="empty-state-title">No machines found</h3>
                        <p className="empty-state-description">Machines will appear here when bots register</p>
                    </div>
                ) : (
                    <Pagination />
                )}
            </div>

            {/* Bulk Activate Modal */}
            <AnimatePresence>
                {showActivateModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowActivateModal(false)}
                    >
                        <motion.div
                            className="modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3 className="modal-title">
                                    Activate {selectedMachines.length} Machine{selectedMachines.length > 1 ? 's' : ''}
                                </h3>
                                <button className="modal-close" onClick={() => setShowActivateModal(false)}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Info about deactivated machines */}
                                {selectedDeactivated.length > 0 && (
                                    <div style={{
                                        marginBottom: '16px',
                                        padding: '12px',
                                        background: 'rgba(245, 158, 11, 0.1)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(245, 158, 11, 0.2)',
                                        fontSize: '0.85rem'
                                    }}>
                                        <strong style={{ color: 'var(--warning)' }}>Note:</strong>{' '}
                                        {selectedDeactivated.length} deactivated machine{selectedDeactivated.length > 1 ? 's' : ''} will be reactivated with new credentials.
                                        {selectedPending.length > 0 && (
                                            <span> ({selectedPending.length} pending machine{selectedPending.length > 1 ? 's' : ''} will also be activated)</span>
                                        )}
                                    </div>
                                )}
                                <div className="template-selector">
                                    <label className="form-label">
                                        Select Template <span className="required">*</span>
                                    </label>

                                    <div className="template-search-wrapper">
                                        <Search size={14} className="template-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search templates..."
                                            value={templateSearch}
                                            onChange={(e) => setTemplateSearch(e.target.value)}
                                            className="template-search-input"
                                        />
                                    </div>

                                    <div className="template-list">
                                        {filteredTemplates.length === 0 ? (
                                            <div className="template-list-empty">
                                                No templates found
                                            </div>
                                        ) : (
                                            filteredTemplates.map(t => (
                                                <div
                                                    key={t.id}
                                                    className={`template-list-item ${selectedTemplate === String(t.id) ? 'selected' : ''}`}
                                                    onClick={() => setSelectedTemplate(String(t.id))}
                                                >
                                                    <div className="template-list-info">
                                                        <div className="template-list-name">{t.name}</div>
                                                        {t.campaign_name && (
                                                            <div className="template-list-campaign">{t.campaign_name}</div>
                                                        )}
                                                    </div>
                                                    <div className="template-list-meta">
                                                        <div className="template-list-sips">{t.sip_count || 0} SIPs</div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {selectedTemplate && (
                                    <div className="template-preview">
                                        <div className="template-preview-title">Selected Template</div>
                                        {(() => {
                                            const t = templates.find(t => String(t.id) === selectedTemplate)
                                            if (!t) return null
                                            return (
                                                <>
                                                    <div className="template-preview-row">
                                                        <span className="template-preview-label">Name:</span>
                                                        <span className="template-preview-value">{t.name}</span>
                                                    </div>
                                                    <div className="template-preview-row">
                                                        <span className="template-preview-label">Campaign:</span>
                                                        <span className="template-preview-value">{t.campaign_name || '-'}</span>
                                                    </div>
                                                    <div className="template-preview-row">
                                                        <span className="template-preview-label">Avatar:</span>
                                                        <span className="template-preview-value">{t.avatar_name || '-'}</span>
                                                    </div>
                                                    <div className="template-preview-row">
                                                        <span className="template-preview-label">Dialer:</span>
                                                        <span className="template-preview-value">{t.dialer_name || '-'}</span>
                                                    </div>
                                                    <div className="template-preview-row">
                                                        <span className="template-preview-label">Available SIPs:</span>
                                                        <span className="template-preview-value">{(t.sip_count || 0) - (t.licenses_count || 0)}</span>
                                                    </div>
                                                </>
                                            )
                                        })()}
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    onClick={() => setShowActivateModal(false)}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleBulkActivate}
                                    disabled={activating || !selectedTemplate}
                                    className="btn btn-primary"
                                >
                                    {activating ? (
                                        <>
                                            <Loader2 size={14} className="spinner" />
                                            Activating...
                                        </>
                                    ) : (
                                        <>
                                            <Play size={14} />
                                            Activate
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Edit License Modal */}
            <AnimatePresence>
                {showEditModal && editingMachine && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowEditModal(false)}
                    >
                        <motion.div
                            className="modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3 className="modal-title">
                                    Edit License: {editingMachine.sip_id}
                                </h3>
                                <button className="modal-close" onClick={() => setShowEditModal(false)}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* No License Alert */}
                                {editFormData._hasNoLicense && (
                                    <div style={{
                                        marginBottom: '16px',
                                        padding: '12px',
                                        background: 'rgba(245, 158, 11, 0.1)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(245, 158, 11, 0.2)',
                                        fontSize: '0.85rem'
                                    }}>
                                        <strong style={{ color: 'var(--warning)' }}>No License Found</strong>
                                        <div style={{ marginTop: '4px', color: 'var(--text-secondary)' }}>
                                            Select a template below to create a license with the next available SIP.
                                        </div>
                                    </div>
                                )}

                                {/* Template Selector */}
                                <div className="form-group" style={{
                                    padding: '12px',
                                    background: 'var(--bg-secondary)',
                                    borderRadius: '8px',
                                    marginBottom: '16px'
                                }}>
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        Apply Template
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                                            (auto-fills all fields)
                                        </span>
                                    </label>
                                    <select
                                        value={editFormData.template_id || ''}
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                applyTemplateToEditForm(e.target.value)
                                            } else {
                                                setEditFormData(prev => ({ ...prev, template_id: '' }))
                                            }
                                        }}
                                        className="form-select"
                                    >
                                        <option value="">-- Select Template --</option>
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} {t.campaign_name ? `(${t.campaign_name})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Password <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={editFormData.password}
                                        onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                                        className="form-input"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Dialer <span className="required">*</span></label>
                                    <select
                                        value={editFormData.dialer_id}
                                        onChange={(e) => setEditFormData({ ...editFormData, dialer_id: e.target.value })}
                                        className="form-select"
                                    >
                                        <option value="">Select Dialer</option>
                                        {dialers.map(d => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Avatar <span className="required">*</span></label>
                                    <select
                                        value={editFormData.avatar_id}
                                        onChange={(e) => setEditFormData({ ...editFormData, avatar_id: e.target.value })}
                                        className="form-select"
                                    >
                                        <option value="">Select Avatar</option>
                                        {avatars.map(a => (
                                            <option key={a.id} value={a.id}>{a.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Access Link <span className="required">*</span></label>
                                    <select
                                        value={editFormData.access_link}
                                        onChange={(e) => setEditFormData({ ...editFormData, access_link: e.target.value })}
                                        className="form-select"
                                    >
                                        <option value="">Select Link</option>
                                        {accessLinks.map((link, idx) => (
                                            <option key={idx} value={link}>{link}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Campaign ID <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={editFormData.campaign}
                                        onChange={(e) => setEditFormData({ ...editFormData, campaign: e.target.value })}
                                        className="form-input"
                                        placeholder="e.g., CAMP001"
                                    />
                                </div>

                                {/* Bot Configuration Section */}
                                <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '16px', paddingTop: '16px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setEditFormData({ ...editFormData, _showBotConfig: !editFormData._showBotConfig })}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'var(--text-secondary)',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: 0
                                        }}
                                    >
                                        {editFormData._showBotConfig ? '▼' : '▶'} Bot Configuration
                                    </button>

                                    {editFormData._showBotConfig && (
                                        <div style={{ marginTop: '12px', background: 'var(--bg-secondary)', padding: '12px', borderRadius: '8px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                <div className="form-group" style={{ marginBottom: '8px' }}>
                                                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Login Method</label>
                                                    <select className="form-select" value={editFormData.login_method || 'double-merged'} onChange={e => setEditFormData({ ...editFormData, login_method: e.target.value })}>
                                                        <option value="single-v1">Single V1</option>
                                                        <option value="single-v2">Single V2</option>
                                                        <option value="double">Double</option>
                                                        <option value="double-merged">Double Merged</option>
                                                        <option value="second-try">Second Try</option>
                                                    </select>
                                                </div>
                                                <div className="form-group" style={{ marginBottom: '8px' }}>
                                                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Transfer Method</label>
                                                    <select className="form-select" value={editFormData.transfer_method || 'd1'} onChange={e => setEditFormData({ ...editFormData, transfer_method: e.target.value })}>
                                                        <option value="dynamic">Dynamic</option>
                                                        <option value="inboundlocal">Inbound Local</option>
                                                        <option value="d1">D1</option>
                                                        <option value="d2">D2</option>
                                                        <option value="d3">D3</option>
                                                        <option value="d4">D4</option>
                                                        <option value="d5">D5</option>
                                                        <option value="d2-preset">D2 Preset</option>
                                                        <option value="Quick">Quick</option>
                                                        <option value="local">Local</option>
                                                        <option value="local-group">Local Group</option>
                                                        <option value="preset-dial-with-customer">Preset Dial With Customer</option>
                                                        <option value="dial-with-customer">Dial With Customer</option>
                                                        <option value="d1-preset">D1 Preset</option>
                                                        <option value="d4-preset">D4 Preset</option>
                                                        <option value="preset-voice">Preset Voice</option>
                                                        <option value="d1-cons-dwc">D1 Cons DWC</option>
                                                        <option value="d1-dial-with-customer">D1 Dial With Customer</option>
                                                        <option value="localcloserconsultative">Local Closer Consultative</option>
                                                    </select>
                                                </div>
                                                <div className="form-group" style={{ marginBottom: '8px' }}>
                                                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Transfer Disposition</label>
                                                    <select className="form-select" value={editFormData.xfer_disposition_selector || 'call-transfered'} onChange={e => setEditFormData({ ...editFormData, xfer_disposition_selector: e.target.value })}>
                                                        <option value="call-transfered">Call Transfered</option>
                                                        <option value="sale-made">Sale Made</option>
                                                    </select>
                                                </div>
                                                <div className="form-group" style={{ marginBottom: '8px' }}>
                                                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Transcriber</label>
                                                    <select className="form-select" value={editFormData.transcriber_type || 'VOSK'} onChange={e => setEditFormData({ ...editFormData, transcriber_type: e.target.value })}>
                                                        <option value="VOSK">VOSK</option>
                                                        <option value="WHISPER_S1">Whisper S1</option>
                                                        <option value="WHISPER_S2">Whisper S2</option>
                                                        <option value="WHISPER_S3">Whisper S3</option>
                                                        <option value="WHISPER_S4">Whisper S4</option>
                                                    </select>
                                                </div>
                                                <div className="form-group" style={{ marginBottom: '8px' }}>
                                                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Number of Audios</label>
                                                    <input type="number" min="1" max="10" className="form-input" value={editFormData.number_of_audios || 1} onChange={e => setEditFormData({ ...editFormData, number_of_audios: parseInt(e.target.value) || 1 })} />
                                                </div>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={editFormData.dont_wait} onChange={e => setEditFormData({ ...editFormData, dont_wait: e.target.checked })} />
                                                    Don't Wait
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={editFormData.comment_login_submit_btn} onChange={e => setEditFormData({ ...editFormData, comment_login_submit_btn: e.target.checked })} />
                                                    Skip Login Btn
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={editFormData.comment_2nd_hello_logic} onChange={e => setEditFormData({ ...editFormData, comment_2nd_hello_logic: e.target.checked })} />
                                                    Skip 2nd Hello
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={editFormData.cmnt_40sec_call_limit_check} onChange={e => setEditFormData({ ...editFormData, cmnt_40sec_call_limit_check: e.target.checked })} />
                                                    Skip 40s Limit
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={editFormData.dispo_in_cb_after_40sec} onChange={e => setEditFormData({ ...editFormData, dispo_in_cb_after_40sec: e.target.checked })} />
                                                    Dispo CB 40s
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={editFormData.debug_mode} onChange={e => setEditFormData({ ...editFormData, debug_mode: e.target.checked })} />
                                                    Debug Mode
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={saving}
                                    className="btn btn-primary"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 size={14} className="spinner" />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default Machines